<template>
  <div class="app">
    <app-header v-if="!miniLayout" />
    <router-view v-if="status === 200" class="view" />
    <ErrorPage v-if="status !== 200" :status="status" />
    <seo-text-page />
    <to-top v-if="showToTop" />
    <app-footer :location="miniLayout ? 'footer_menu_checkout' : 'footer_menu'" />
    <GlobalSearch v-if="!miniLayout && isSearchShown" />
    <QuickView v-if="quickViewModel?.id" />

    <product-gallery-modal v-if="isProductGalleryShown" />
    <Notifications />
    <auth-modal v-if="$auth.data.isAuthShown" />
    <city-modal v-if="$city.data.isCityModalShown" />
    <city-confirmation-modal v-if="$city.data.isCityConfirmationShown" />
    <cookies-modal v-if="isCookieModalShown" />
    <antibot-modal />
    <bonus-modal />
    <cart-merge-modal v-if="isNeedMergeCarts" />
    <barcode-scanner-modal v-if="isScannerFeatureEnabled" />
    <order-popup v-if="isOrderPopupShown" />
    <club-modal />
  </div>
</template>

<script>
import { useHead } from '@vueuse/head';
import {
  computed,
  defineAsyncComponent,
  inject,
  onMounted,
  onServerPrefetch,
  ref,
  useSSRContext,
} from 'vue';
import { useRoute } from 'vue-router';
import {
  mapActions, mapGetters, mapMutations, mapState,
  useStore,
} from 'vuex';

import features from '@common/constants/features';

import SeoTextPage from '@/components/app-kit/seo-text-page/seo-text-page';
import AntibotModal from '@/components/common/antibot-modal/antibot-modal.vue';
// TODO: проверить можно ли сделать Lazy
import BonusModal from '@/components/common/bonus-modal/bonus-modal.vue';
import CityModal from '@/components/common/city-modal.vue';
import { useShowBarcodeScanner } from '@/composables/useShowBarcodeScanner';
import { commonGtmEvents } from '@/gtm/common-gtm-events';
import { getCookieAgreement } from '@/utils/cookies/agreement-cookies';
import appScripts from '@/utils/system/app-scripts';

import AppFooter from './components/footer/index';
import AppHeader from './components/header/index';
import Notifications from './components/notifications/notifications.vue';
import ToTop from './components/to-top/to-top.vue';
import ErrorPage from './pages/error';
import { useMindboxAnalytics } from './useMindboxAnalytics';

import './assets/scss/app.scss';

export default {
  name: 'App',

  components: {
    CartMergeModal: defineAsyncComponent(() => import('./components/common/cart-merge-modal/cart-merge-modal')),
    CityConfirmationModal: defineAsyncComponent(() => import('./components/common/city-confirmation-modal')),
    CookiesModal: defineAsyncComponent(() => import('./components/common/cookies-modal')),
    AppHeader,
    AppFooter,
    GlobalSearch: defineAsyncComponent(() => import('@/components/search/global-search')),
    Notifications,
    ErrorPage,
    ToTop,
    QuickView: defineAsyncComponent(() => import('@/components/product/quickview.vue')),
    AuthModal: defineAsyncComponent(() => import('@/components/common/auth-modal.vue')),
    CityModal,
    AntibotModal,
    BonusModal,
    ProductGalleryModal: defineAsyncComponent(() => import('./components/product/product-gallery-modal/product-gallery-modal.vue')),
    BarcodeScannerModal: defineAsyncComponent(() => import('./components/barcode-scanner/barcode-scanner-modal.vue')),
    OrderPopup: defineAsyncComponent(() => import('@/components/account/order/order-popup.vue')),
    SeoTextPage,
    ClubModal: defineAsyncComponent(() => import('@/components/common/club-modal/club-modal.vue')),
  },

  inject: ['adaptive', 'thirdPartyScriptsDisabled'],

  setup() {
    const thirdPartyScriptsDisabled = inject('thirdPartyScriptsDisabled');
    const store = useStore();
    const route = useRoute();
    const meta = [
      {
        name: 'format-detection',
        content: 'telephone=no',
      },
      {
        vmid: 'charset',
        charset: 'utf-8',
        once: true,
      },
      {
        vmid: 'viewport',
        name: 'viewport',
        content: 'width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=1',
        once: true,
      },
      {
        name: 'yandex-verification',
        content: 'f352df3ce70c9d12',
      },
      {
        property: 'og:type',
        content: 'website',
      },
      {
        property: 'og:site_name',
        content: 'Сеть магазинов одежды для всей семьи Funday',
      },
      {
        property: 'og:image',
        content: 'https://cdn.fundayshop.com/upload/content/root_static/ru_funday/statics/logo-funday.jpg',
      },
    ];

    const link = [];

    const SEO = computed(() => store.getters['seo/getSeo']);
    const hasQuickViewModel = computed(() => Boolean(store.getters['quickView/model']?.id));

    const getScripts = () => {
      if (thirdPartyScriptsDisabled) return {};

      const isOnlineChat = computed(() => store.getters['features/$isFeatureEnabled'](features.ONLINE_CHAT));

      const appScriptsSettings = {
        isTalkMeEnabled: isOnlineChat.value,
      };

      return appScripts(appScriptsSettings);
    };

    onServerPrefetch(async () => {
      if (route?.fullPath && route?.fullPath !== '/favicon.ico') {
        await store.dispatch('settings/fetchSettings');
        await store.dispatch('seo/loadSeo', { url: route?.fullPath });

        meta.push({
          property: 'og:url',
          content: `https://fundayshop.com${route.fullPath}`,
        });

        if (SEO.value.description) {
          meta.push({
            vmid: 'description',
            name: 'description',
            content: SEO.value.description,
          });

          meta.push({
            property: 'og:description',
            content: SEO.value.description,
          });
        }

        if (SEO.value.canonical) {
          link.push({
            vmid: 'canonical',
            rel: 'canonical',
            href: SEO.value.canonical,
          });
        }

        if (SEO.value.title) {
          meta.push({
            property: 'og:title',
            content: `${SEO.value.title} — Официальный интернет-магазин Funday`,
          });
        }

        useHead({
          title: SEO.value.title ?? '',
          templateParams: {
            separator: '—',
            text: 'Официальный интернет-магазин Funday',
          },
          titleTemplate: '%s %separator %text',
          meta,
          link,
          ...getScripts(),
        }, {
          mode: 'server',
        });
      }
    });

    const { isScannerFeatureEnabled } = useShowBarcodeScanner();

    const { sendEvent } = useMindboxAnalytics();
    sendEvent();

    const $order = inject('$order');
    const isOrderPopupShown = computed(() => route.name !== 'AccountOrders' && $order.data.isShown);

    const isCookieModalShown = ref(false);
    onMounted(() => {
      isCookieModalShown.value = !getCookieAgreement();
    });

    const isProductGalleryShown = computed(() => store.getters['productGalleryModal/isGalleryShown']);
    const isSearchShown = computed(() => store.getters['search/isSearchShown']);

    return {
      isScannerFeatureEnabled,
      hasQuickViewModel,
      isOrderPopupShown,
      isCookieModalShown,
      isProductGalleryShown,
      isSearchShown,
    };
  },

  computed: {
    ...mapState('user', {
      DEFAULT_CITY_ID: 'defaultCityId',
      CITY_CONFIRMED: 'cityConfirmed',
    }),

    ...mapState('config', {
      IP: 'ip',
    }),

    ...mapGetters({
      status: 'config/status',
      account: 'user/account',
      cityId: 'user/cityId',
      cityName: 'user/cityName',
      quickViewModel: 'quickView/model',
      isNeedMergeCarts: 'user/isNeedMergeCarts',
    }),

    miniLayout() {
      const route = useRoute();
      return ['Checkout'].includes(route.name);
    },

    showToTop() {
      const route = useRoute();
      return ['Product', 'Catalog', 'Category'].includes(route.name);
    },

  },

  mounted() {
    const route = useRoute();
    this.$persEvents.sendPageViewEvent();

    if (!this.CITY_CONFIRMED) {
      this.DETECT_CITY();
    }

    const city = {
      id: this.cityId,
      name: this.cityName,
    };

    commonGtmEvents.sendUserAccount(city, this.account, { ip: this.IP });
    this.$persEvents.debouncedEventBanners();

    this.initFreeShippingNotify(route.name);
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.$persEvents.debouncedEventBanners);
    this.$persEvents.sendBannersAppearEvent();
  },

  created() {
    if (typeof window === 'undefined') return;

    this.adaptive.initMatchMedia();
    window.addEventListener('scroll', this.$persEvents.debouncedEventBanners);
  },

  async serverPrefetch() {
    const ctx = useSSRContext();

    this.SET_STATUS(ctx.status);
    this.SET_ACCESS_TOKEN(ctx.accessToken);
    this.SET_OSTIN_ACCESS_TOKEN(ctx.ostinAccessToken);

    if (!ctx.cookies.CITY_ID || /\D/g.test(ctx.cookies.CITY_ID)) {
      this.SET_CITY_ID(this.DEFAULT_CITY_ID);
      this.CONFIRM_CITY(false);
    } else {
      this.SET_CITY_ID(ctx.cookies.CITY_ID);
      this.CONFIRM_CITY(true);
    }
    await this.GET_ACCOUNT();

    return Promise.all([
      this.GET_CITY(),
      this.LOAD_AB_TEST(),
    ]);
  },

  methods: {
    ...mapActions({
      GET_ACCOUNT: 'user/getAccount',
      GET_CITY: 'user/getCity',
      DETECT_CITY: 'user/detectCity',
      LOAD_AB_TEST: 'config/loadABTest',
      initFreeShippingNotify: 'freeShippingNotify/init',
    }),

    ...mapMutations({
      SET_CITY_ID: 'user/setCityId',
      CONFIRM_CITY: 'user/cityConfirmed',
      SET_STATUS: 'config/setStatus',
      SET_ACCESS_TOKEN: 'config/setAccessToken',
      SET_OSTIN_ACCESS_TOKEN: 'config/setOstinAccessToken',
    }),
  },
};
</script>

<style lang="scss">
// body-scroll-lock package
.modal-opened {
  overflow: hidden;
}

.app {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 100%;

  .view {
    margin-bottom: $mobileMarginBottom;
  }

  .footer {
    margin-top: auto;
  }
}

@include breakpoint("tablet") {
  .app {
    .view {
      margin-bottom: $tabletMarginBottom;
    }
  }
}

@include breakpoint("desktop") {
  // body-scroll-lock package
  .modal-opened {
    overflow: auto;
  }

  .app {
    .view {
      margin-bottom: $desktopMarginBottom;
    }
  }
}

</style>
